import React, { useEffect, useState } from "react";
import { useAppSelector } from "../../../redux/typedHooks";
import type { UserInterface } from "../../../types/user";
import { createAppealAPI, getFirstUnviewedDeletedProfileAPI, getUserBanStatusAPI, markProfileReportAsViewedAPI } from "../../../api/userApi";
import BannedUserPage from "./BannedUserPage";
import { showToast } from "../../../utils/toast";
import OnboardingLayout from "../../../layouts/onboarding.layout";
import { Box, Typography } from "@material-ui/core";

const BannedUserModalManager: React.FC = () => {
  const user = useAppSelector<UserInterface | null>(
    state => state.user.currentUser
  );
  const [banDuration, setBanDuration] = useState<{ days: number; hours: number; minutes: number } | null>(null)

  const fetchUnviewedProfile = async () => {
    try {
      return await getFirstUnviewedDeletedProfileAPI();
    } catch (error: any) {
      console.error("Error fetching unviewed profile:", error);
    }
  }

  const onSubmitAppeal = async (comment: string) => {
    if (!comment.trim()) {
      showToast("Please provide a comment for your appeal", "error");
      return;
    }
    if (!user) return console.error("User not found");
    try {
      const response = await createAppealAPI(
        user._id,
        "profile",
        "Appeal",// Default reason
        comment.trim()
      );
      if (response.success) {
        showToast("Your appeal has been submitted and will be reviewed", "success");
        // Mark as viewed after appeal submission;
        try {
          const responseUnviewed = await fetchUnviewedProfile()
          await markProfileReportAsViewedAPI(responseUnviewed.data._id);
        } catch (error) {
          console.error("Error marking post as viewed:", error);
        }
      } else {
        showToast(response.message || "Failed to submit appeal", "error");
      }
    } catch (error: any) {
      showToast(
        error?.response?.data?.message ||
        error?.message ||
        "Failed to submit appeal",
        "error"
      );
    }
  };

  useEffect(() => {
    (async () => {
      if (user && user._id) {
        const response = await getUserBanStatusAPI(user._id);
        if (response.success) {

          if (response.data.banExpiresAt) {
            const expiresAt = new Date(response.data.banExpiresAt).getTime();
            const now = Date.now();
            const diffMs = expiresAt - now;

            if (diffMs > 0) {
              const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
              const diffHours = Math.floor((diffMs / (1000 * 60 * 60)) % 24);
              const diffMinutes = Math.floor((diffMs / (1000 * 60)) % 60);

              setBanDuration({ days: diffDays, hours: diffHours, minutes: diffMinutes });
            } else {
              setBanDuration({ days: 0, hours: 0, minutes: 0 });
            }
          } else {
            setBanDuration(null); // permanent ban
          }
        }
      }
    })();
  }, [user]);

  return (
    <OnboardingLayout
      showHeader={true}
      title="User Banned"
      subtitle="Your account has been banned. You can appeal this decision or leave the page."
      maxWidth={1195}
      hasSidebar={true}
    >
      <Box textAlign={"center"}>
        {banDuration && (
          <Typography variant="body2" style={{ color: "#f44336", marginTop: "8px" }}>
            Ban expires in {banDuration.days} days, {banDuration.hours} hours, {banDuration.minutes} minutes
          </Typography>
        )}

        {banDuration === null && (
          <Typography variant="body2" style={{ color: "#f44336", marginTop: "8px" }}>
            This account is permanently banned
          </Typography>
        )}
      </Box>
      <BannedUserPage onSubmitAppeal={onSubmitAppeal} />
    </OnboardingLayout>
  );
};

export default BannedUserModalManager;
